import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageCircle, Send, X, Sparkles, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const WELCOME_MESSAGE = {
    role: "assistant",
    content:
        "Hi there. I am your BlueLedger assistant. Ask me anything about the app, like how to create an invoice, import from Zoho, or turn off Cayman no-tax mode. I will do my best to help.",
};

export default function AssistantWidget() {
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([WELCOME_MESSAGE]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages, open]);

    const send = async () => {
        const text = input.trim();
        if (!text || busy) return;
        const nextMessages = [...messages, { role: "user", content: text }];
        setMessages(nextMessages);
        setInput("");
        setBusy(true);
        try {
            const { data } = await api.post("/assistant/chat", {
                messages: nextMessages
                    .filter((m) => m.role === "user" || m.role === "assistant")
                    .slice(-12),
                session_id: sessionId,
            });
            setSessionId(data.session_id);
            setMessages((prev) => [...prev, { role: "assistant", content: data.reply }]);
        } catch (e) {
            setMessages((prev) => [
                ...prev,
                {
                    role: "assistant",
                    content:
                        "I hit a snag trying to answer that. Please try again in a moment, or browse the Help Center in the sidebar for a hand-written guide.",
                },
            ]);
        } finally {
            setBusy(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const reset = () => {
        setMessages([WELCOME_MESSAGE]);
        setSessionId(null);
    };

    return (
        <>
            {/* Floating launcher */}
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="assistant-launcher"
                className={`fixed z-40 bottom-6 right-6 h-14 w-14 rounded-full shadow-card-hover grid place-items-center transition-all ${
                    open ? "bg-ocean-700 text-white" : "bg-deep-ocean text-white hover:scale-105"
                }`}
                aria-label="Open assistant"
            >
                {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
                {!open && (
                    <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-coral ring-2 ring-sand-50" />
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.98 }}
                        transition={{ duration: 0.18 }}
                        data-testid="assistant-panel"
                        className="fixed z-40 bottom-24 right-6 w-[min(92vw,400px)] h-[560px] max-h-[calc(100vh-120px)] rounded-3xl bg-white border border-sand-200 shadow-card-hover overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="relative bg-deep-ocean text-white p-5">
                            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-secondary/30 blur-3xl" />
                            <div className="relative flex items-start justify-between">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-secondary-light font-semibold inline-flex items-center gap-1.5">
                                        <Sparkles className="h-3 w-3" /> Virtual Assistant
                                    </div>
                                    <div className="mt-1 font-heading text-xl">Ask BlueLedger</div>
                                    <div className="text-xs text-white/70 mt-0.5">Trained on every help article in the app</div>
                                </div>
                                <button onClick={reset} className="text-[11px] text-white/60 hover:text-white transition-colors" title="Start a new chat">
                                    Reset
                                </button>
                            </div>
                        </div>

                        {/* Messages */}
                        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin bg-sand-50">
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    data-testid={`msg-${m.role}-${i}`}
                                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap ${
                                        m.role === "user"
                                            ? "ml-auto bg-ocean-700 text-white rounded-br-sm"
                                            : "bg-white border border-sand-200 text-ocean-700 rounded-bl-sm shadow-soft"
                                    }`}
                                >
                                    {m.content}
                                </div>
                            ))}
                            {busy && (
                                <div className="bg-white border border-sand-200 rounded-2xl rounded-bl-sm max-w-[85%] px-4 py-3 shadow-soft inline-flex items-center gap-2 text-muted-foreground text-sm">
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> thinking
                                </div>
                            )}
                        </div>

                        {/* Composer */}
                        <div className="border-t border-sand-200 p-3 bg-white">
                            <div className="flex gap-2">
                                <Input
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    placeholder="Ask anything about BlueLedger"
                                    disabled={busy}
                                    className="h-11 rounded-xl border-sand-300"
                                    data-testid="assistant-input"
                                />
                                <Button onClick={send} disabled={busy || !input.trim()} className="h-11 w-11 p-0 bg-ocean-700 hover:bg-ocean-700/90 text-white rounded-xl" data-testid="assistant-send">
                                    <Send className="h-4 w-4" />
                                </Button>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-2 text-center">
                                Answers are AI-generated. Verify anything important with your accountant.
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
